type WelcomePdfData = {
  tenantName: string;
  tenantSlug: string;
  ownerName: string;
  ownerEmail: string;
  temporaryPassword: string;
  loginUrl: string;
};

const ascii = (value: string) =>
  value.normalize("NFKD").replace(/[^\x20-\x7E]/g, "");

const pdfEscape = (value: string) =>
  ascii(value).replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");

const wrap = (value: string, max = 78) => {
  const words = ascii(value).split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    if (!line) line = word;
    else if ((line + " " + word).length <= max) line += " " + word;
    else { lines.push(line); line = word; }
  }
  if (line) lines.push(line);
  return lines;
};

export function buildWelcomePdf(data: WelcomePdfData): Blob {
  const commands: string[] = [
    "0.055 0.165 0.29 rg",
    "0 690 612 102 re f",
    "1 1 1 rg",
    "BT /F2 24 Tf 54 744 Td (RemediKeep) Tj ET",
    "BT /F1 11 Tf 54 722 Td (Secure Login Instructions) Tj ET",
    "0.055 0.165 0.29 rg",
    "BT /F2 16 Tf 54 654 Td (Welcome to RemediKeep) Tj ET",
    "0.25 0.31 0.39 rg",
    `BT /F1 11 Tf 54 632 Td (Workspace: ${pdfEscape(data.tenantName)}) Tj ET`,
    `BT /F1 11 Tf 54 614 Td (Account name: ${pdfEscape(data.ownerName)}) Tj ET`,
    "0.92 0.95 0.98 rg",
    "46 474 520 112 re f",
    "0.055 0.165 0.29 rg",
    "BT /F2 12 Tf 62 560 Td (Your one-time login) Tj ET",
    `BT /F1 10 Tf 62 536 Td (Login URL: ${pdfEscape(data.loginUrl)}) Tj ET`,
    `BT /F1 10 Tf 62 516 Td (Email: ${pdfEscape(data.ownerEmail)}) Tj ET`,
    `BT /F2 10 Tf 62 496 Td (Temporary Password: ${pdfEscape(data.temporaryPassword)}) Tj ET`,
    "0.055 0.165 0.29 rg",
    "BT /F2 13 Tf 54 438 Td (First-login steps) Tj ET",
  ];

  [
    "1. Open the login URL above and enter the email and temporary password.",
    "2. Create a new private password when prompted.",
    "3. Complete multi-factor authentication (MFA) setup and save recovery codes safely.",
    "4. Confirm the workspace name before adding users, clients, or operational records.",
  ].forEach((step, index) => {
    wrap(step).forEach((line, lineIndex) => {
      const y = 414 - index * 38 - lineIndex * 14;
      commands.push(`BT /F1 10 Tf 62 ${y} Td (${pdfEscape(line)}) Tj ET`);
    });
  });

  commands.push(
    "0.78 0.18 0.12 rg",
    "BT /F2 12 Tf 54 242 Td (Security notice) Tj ET",
    "0.25 0.31 0.39 rg",
  );
  [
    "This PDF contains a one-time password. Send it only to the intended account user.",
    "Do not forward it through group chats or shared mailboxes. Delete it after first login.",
    "If the file reaches the wrong person, ask your administrator to issue a new temporary login.",
  ].forEach((paragraph, index) => {
    wrap(paragraph).forEach((line, lineIndex) => {
      const y = 220 - index * 34 - lineIndex * 14;
      commands.push(`BT /F1 9 Tf 62 ${y} Td (${pdfEscape(line)}) Tj ET`);
    });
  });

  commands.push(
    "0.55 0.61 0.67 rg",
    "BT /F1 8 Tf 54 72 Td (Support: management@rmbcservices.com) Tj ET",
    `BT /F1 8 Tf 54 58 Td (Workspace reference: ${pdfEscape(data.tenantSlug)}) Tj ET`,
    "BT /F1 8 Tf 54 44 Td (RemediKeep by RMBCS - Confidential credential document) Tj ET",
  );

  const stream = commands.join("\n");
  const objects = [
    "<< /Type /Catalog /Pages 2 0 R >>",
    "<< /Type /Pages /Kids [3 0 R] /Count 1 >>",
    "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 5 0 R /F2 6 0 R >> >> /Contents 4 0 R >>",
    `<< /Length ${new TextEncoder().encode(stream).length} >>\nstream\n${stream}\nendstream`,
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>",
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>",
  ];

  let pdf = "%PDF-1.4\n%RemediKeep\n";
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(new TextEncoder().encode(pdf).length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });
  const xrefOffset = new TextEncoder().encode(pdf).length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`;
  return new Blob([pdf], { type: "application/pdf" });
}
